import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import type { EditorJsContent } from '../../../shared/models/blog.models';

@Component({
  selector: 'app-blog-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cg-blog-prose">
      @for (block of blocks; track $index) {
        @switch (block.type) {
          @case ('header') {
            @if (headerLevel(block) === 2) {
              <h2 [innerHTML]="safeHtml(headerText(block))"></h2>
            } @else if (headerLevel(block) === 3) {
              <h3 [innerHTML]="safeHtml(headerText(block))"></h3>
            } @else {
              <h4 [innerHTML]="safeHtml(headerText(block))"></h4>
            }
          }
          @case ('paragraph') {
            <p [innerHTML]="safeHtml(paragraphText(block))"></p>
          }
          @case ('list') {
            @if (isOrderedList(block)) {
              <ol>
                @for (item of listItems(block); track $index) {
                  <li [innerHTML]="safeHtml(item)"></li>
                }
              </ol>
            } @else {
              <ul>
                @for (item of listItems(block); track $index) {
                  <li [innerHTML]="safeHtml(item)"></li>
                }
              </ul>
            }
          }
          @case ('image') {
            <figure class="cg-blog-prose__figure">
              <img
                [src]="imageUrl(block)"
                [alt]="imageAlt(block)"
                loading="lazy"
                class="cg-blog-prose__img"
              />
              @if (imageCaption(block)) {
                <figcaption [innerHTML]="safeHtml(imageCaption(block))"></figcaption>
              }
            </figure>
          }
          @case ('quote') {
            <blockquote class="cg-blog-prose__quote">
              <p [innerHTML]="safeHtml(quoteText(block))"></p>
              @if (quoteCaption(block)) {
                <cite [innerHTML]="safeHtml(quoteCaption(block))"></cite>
              }
            </blockquote>
          }
          @case ('delimiter') {
            <hr class="cg-blog-prose__delimiter" />
          }
          @case ('warning') {
            <aside class="cg-blog-prose__warning">
              @if (warningTitle(block)) {
                <strong [innerHTML]="safeHtml(warningTitle(block))"></strong>
              }
              <p [innerHTML]="safeHtml(warningMessage(block))"></p>
            </aside>
          }
          @case ('checklist') {
            <ul class="cg-blog-prose__checklist">
              @for (item of checklistItems(block); track $index) {
                <li [class.cg-blog-prose__checklist-item--checked]="item.checked">
                  <span aria-hidden="true">{{ item.checked ? '✓' : '○' }}</span>
                  <span [innerHTML]="safeHtml(item.text)"></span>
                </li>
              }
            </ul>
          }
          @case ('table') {
            <div class="cg-blog-prose__table-wrap">
              <table>
                @if (tableWithHeadings(block)) {
                  <thead>
                    <tr>
                      @for (cell of tableHead(block); track $index) {
                        <th [innerHTML]="safeHtml(cell)"></th>
                      }
                    </tr>
                  </thead>
                }
                <tbody>
                  @for (row of tableBody(block); track $index) {
                    <tr>
                      @for (cell of row; track $index) {
                        <td [innerHTML]="safeHtml(cell)"></td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          @case ('embed') {
            @if (embedUrl(block)) {
              <div class="cg-blog-prose__embed">
                <iframe
                  [src]="embedUrl(block)"
                  title="Embedded content"
                  loading="lazy"
                  allowfullscreen
                ></iframe>
              </div>
            }
          }
          @case ('faq') {
            <div class="cg-blog-prose__faq">
              @for (item of faqItems(block); track item.question) {
                <details class="cg-blog-prose__faq-item">
                  <summary [innerHTML]="safeHtml(item.question)"></summary>
                  <p [innerHTML]="safeHtml(item.answer)"></p>
                </details>
              }
            </div>
          }
        }
      }
    </div>
  `,
})
export class BlogRendererComponent {
  @Input() content: EditorJsContent | null = null;

  constructor(private readonly sanitizer: DomSanitizer) {}

  get blocks() {
    return this.content?.blocks ?? [];
  }

  safeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  headerLevel(block: { data: Record<string, unknown> }): number {
    return Number(block.data['level'] ?? 2);
  }

  headerText(block: { data: Record<string, unknown> }): string {
    return String(block.data['text'] ?? '');
  }

  paragraphText(block: { data: Record<string, unknown> }): string {
    return String(block.data['text'] ?? '');
  }

  isOrderedList(block: { data: Record<string, unknown> }): boolean {
    return block.data['style'] === 'ordered';
  }

  listItems(block: { data: Record<string, unknown> }): string[] {
    const items = block.data['items'];
    if (!Array.isArray(items)) return [];
    return items.map((item) => (typeof item === 'string' ? item : String((item as { content?: string }).content ?? '')));
  }

  imageUrl(block: { data: Record<string, unknown> }): string {
    const file = block.data['file'] as { url?: string } | undefined;
    return String(file?.url ?? block.data['url'] ?? '');
  }

  imageAlt(block: { data: Record<string, unknown> }): string {
    return String(block.data['alt'] ?? block.data['caption'] ?? '');
  }

  imageCaption(block: { data: Record<string, unknown> }): string {
    return String(block.data['caption'] ?? '');
  }

  quoteText(block: { data: Record<string, unknown> }): string {
    return String(block.data['text'] ?? '');
  }

  quoteCaption(block: { data: Record<string, unknown> }): string {
    return String(block.data['caption'] ?? '');
  }

  warningTitle(block: { data: Record<string, unknown> }): string {
    return String(block.data['title'] ?? '');
  }

  warningMessage(block: { data: Record<string, unknown> }): string {
    return String(block.data['message'] ?? '');
  }

  checklistItems(block: { data: Record<string, unknown> }): Array<{ text: string; checked: boolean }> {
    const items = block.data['items'];
    if (!Array.isArray(items)) return [];
    return items.map((item) => ({
      text: String((item as { text?: string }).text ?? ''),
      checked: Boolean((item as { checked?: boolean }).checked),
    }));
  }

  tableWithHeadings(block: { data: Record<string, unknown> }): boolean {
    return Boolean(block.data['withHeadings']);
  }

  tableHead(block: { data: Record<string, unknown> }): string[] {
    const content = block.data['content'] as string[][] | undefined;
    return content?.[0]?.map(String) ?? [];
  }

  tableBody(block: { data: Record<string, unknown> }): string[][] {
    const content = block.data['content'] as string[][] | undefined;
    if (!content?.length) return [];
    return this.tableWithHeadings(block) ? content.slice(1) : content;
  }

  embedUrl(block: { data: Record<string, unknown> }): SafeHtml | null {
    const embed = String(block.data['embed'] ?? block.data['source'] ?? '');
    if (!embed || !this.isAllowedEmbed(embed)) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(embed);
  }

  faqItems(block: { data: Record<string, unknown> }): Array<{ question: string; answer: string }> {
    const items = block.data['items'];
    if (!Array.isArray(items)) return [];
    return items.map((item) => ({
      question: String((item as { question?: string }).question ?? ''),
      answer: String((item as { answer?: string }).answer ?? ''),
    }));
  }

  private isAllowedEmbed(url: string): boolean {
    try {
      const host = new URL(url).hostname.replace(/^www\./, '');
      return ['youtube.com', 'youtu.be', 'vimeo.com', 'player.vimeo.com'].some((h) => host.includes(h));
    } catch {
      return false;
    }
  }
}
